import { createPortal } from 'react-dom';
import { useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import type { FieldArrayRenderProps } from 'formik';
import type { JSX } from 'react';
import type { ReadonlyDeep } from 'type-fest';

import { getFragmentData } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { RightDrawer } from '../../../components/RightDrawer';
import { useActionBlocker } from '../../../hooks/useActionBlocker.hook';
import { useWizardContext } from '../../../components/Wizard/contexts/WizardContext/hooks/useWizardContext.hook';
import { ServiceUsages } from '../../../components/ServiceUsages';
import { ServiceUsagesGroupBanner } from '../../../components/ServiceUsages/GroupBanner';
import { ServiceUsageCardCmp_ResolvableServiceUsage_Fragment } from '../../../components/ServiceUsages/Card/fragments/resolvable-service-usage.fragment';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useContextualizedFields } from '../../../hooks/useContextualizedFields.hook';

import { FormVariant } from '../../enums/variant.enum';
import { ResolvableServiceUsageForm } from '../../ResolvableServiceUsage';
import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../../ResolvableServiceUsage/fragments/resolvable-service-usage.fragment';

import { DeleteResolvableServiceUsageForm } from '../Delete';
import { FlushResolvableServiceUsagesForm } from '../Flush';
import { CustomizeResolvableServiceUsagesForm } from '../Customize';
import type { GroupProps } from '../types/props.type';
import type { ResolvableServiceUsagesContext } from '../types/context.type';

import { useFindResolvableServiceUsageIndexById } from './useFindIndexById.hook';
import { useCreateResolvableServiceUsageSubmit } from './useCreateResolvableServiceUsageSubmit.hook';
import { useFlushAllResolvableServiceUsagesSubmit } from './useFlushAllResolvableServiceUsagesSubmit.hook';
import { useFlushOneResolvableServiceUsageSubmit } from './useFlushOneResolvableServiceUsageSubmit.hook';
import { useCustomizeAllResolvableServiceUsagesSubmit } from './useCustomizeAllResolvableServiceUsagesSubmit.hook';
import { useCustomizeOneResolvableServiceUsageSubmit } from './useCustomizeOneResolvableServiceUsageSubmit.hook';
import { useUpdateResolvableServiceUsageSubmit } from './useUpdateResolvableServiceUsageSubmit.hook';
import { useRemoveResolvableServiceUsageSubmit } from './useRemoveResolvableServiceUsageSubmit.hook';
import { useResolvableServiceUsagesReducer } from './useReducer.hook';

type ContextualizedFields = Readonly<
  Pick<ResolvableServiceUsagesContext, 'serviceUsages'>
>;

export function useResolvableServiceUsagesFieldArrayRenderer(
  props: Readonly<GroupProps>,
): Readonly<{
  fieldArrayRender: (props: Readonly<FieldArrayRenderProps>) => JSX.Element;
}> {
  const {
    documentResolversSettings,
    extraCards,
    fallbackResolvableServiceUsages: pFallbackResolvableServiceUsages,
    formContext,
    groupingMode,
    globalTotal,
    globalTotalErrorMessages,
    groupIsOptional,
    groupLabel,
    groupSectionLabel,
    isInGroup = false,
    manualTotal,
    maskAmounts,
    mutations,
    onStartMove,
    onStartRemoveGroup,
    onStartCreateGroup,
    onStartRenameGroup,
    onStartReorder,
    onStartSwitchGroupingMode,
    paths,
    queryKeysToInvalidate,
    resolvableFieldSettings,
    serviceUsagesGroupId,
    showInheritanceAlert = true,
    showTotalCalculation = !isInGroup,
    storage,
    totalCalculation,
    useDirectMutation,
  } = props;

  const { values, setFieldValue } =
    useFormikContext<ResolvableServiceUsagesContext>();

  const fields = useContextualizedFields<ContextualizedFields>(
    {
      serviceUsages: {
        id: 'service-usages',
        name: 'serviceUsages',
      },
    },
    formContext,
  );

  const serviceUserId = values._id;

  const { serviceUsageId: serviceUsageIdParam } = useParams();

  const { contentOriginsCodeMap } = useReferencesContext();

  const { setIsSubmitLocked } = useWizardContext();

  const fallbackResolvableServiceUsages = getFragmentData(
    ServiceUsageCardCmp_ResolvableServiceUsage_Fragment,
    pFallbackResolvableServiceUsages,
  );

  const canInherit = !!fallbackResolvableServiceUsages;
  const mustInherit = canInherit && !fields.serviceUsages.value;
  const resolvableServiceUsagesOrFallback = mustInherit
    ? fallbackResolvableServiceUsages
    : fields.serviceUsages.value;
  const resolvableServiceUsagesAreMissing =
    !resolvableServiceUsagesOrFallback?.length;

  useEffect(() => {
    setIsSubmitLocked(resolvableServiceUsagesAreMissing);

    return () => {
      setIsSubmitLocked(false);
    };
  }, [resolvableServiceUsagesAreMissing, setIsSubmitLocked]);

  const {
    state,
    handleClearState,
    handleStartCreate,
    handleStartFlushAll,
    handleStartFlushOne,
    handleStartCustomizeAll,
    handleStartCustomizeOne,
    handleStartRemove,
    handleStartUpdate,
    handleCreateSuccess,
    handleFlushAllSuccess,
    handleFlushOneSuccess,
    handleCustomizeAllSuccess,
    handleCustomizeOneSuccess,
    handleUpdateSuccess,
    handleRemoveSuccess,
  } = useResolvableServiceUsagesReducer(
    props,
    (resolvableServiceUsagesOrFallback ?? []).map(
      (serviceUsage) => serviceUsage._id,
    ),
  );

  const findResolvableServiceUsageIndexById =
    useFindResolvableServiceUsageIndexById(props);

  const serviceUsageInitialValue =
    paths && serviceUsageIdParam
      ? fields.serviceUsages.value?.[
          findResolvableServiceUsageIndexById(serviceUsageIdParam as Id)
        ]
      : state.idToUpdate
        ? fields.serviceUsages.value?.[
            findResolvableServiceUsageIndexById(state.idToUpdate)
          ]
        : undefined;

  const { handleSubmitFactory: handleCreateSubmitFactory } =
    useCreateResolvableServiceUsageSubmit({
      directMutation: useDirectMutation ? mutations.create : undefined,
      handleCreateSuccess,
      serviceUsagesGroupId,
      serviceUserId,
    });

  const { handleSubmitFactory: handleFlushAllSubmitFactory } =
    useFlushAllResolvableServiceUsagesSubmit({
      directMutation: useDirectMutation ? mutations.flushAll : undefined,
      handleFlushAllSuccess,
      serviceUserId,
    });

  const { handleSubmitFactory: handleFlushOneSubmitFactory } =
    useFlushOneResolvableServiceUsageSubmit({
      directMutation: useDirectMutation ? mutations.update : undefined,
      formContext,
      handleFlushOneSuccess,
      serviceUsageId: state.idToFlush,
      serviceUsagesGroupId,
      serviceUserId,
    });

  const { handleSubmitFactory: handleCustomizeAllSubmitFactory } =
    useCustomizeAllResolvableServiceUsagesSubmit({
      directMutation: useDirectMutation ? mutations.customizeAll : undefined,
      handleCustomizeAllSuccess,
      serviceUserId,
    });

  const { handleSubmitFactory: handleCustomizeOneSubmitFactory } =
    useCustomizeOneResolvableServiceUsageSubmit({
      directMutation: useDirectMutation ? mutations.update : undefined,
      formContext,
      handleCustomizeOneSuccess,
      serviceUsageId: state.idToCustomize,
      serviceUsagesGroupId,
      serviceUserId,
    });

  const { handleSubmitFactory: handleRemoveSubmitFactory } =
    useRemoveResolvableServiceUsageSubmit({
      directMutation: useDirectMutation ? mutations.remove : undefined,
      formContext,
      handleRemoveSuccess,
      serviceUsageId: state.idToRemove,
      serviceUsagesGroupId,
      serviceUserId,
    });

  const { handleSubmitFactory: handleUpdateSubmitFactory } =
    useUpdateResolvableServiceUsageSubmit({
      directMutation: useDirectMutation ? mutations.update : undefined,
      formContext,
      handleUpdateSuccess,
      serviceUsageId: state.idToUpdate,
      serviceUsagesGroupId,
      serviceUserId,
    });

  const [blockableClearStateHandler, setMustBlockAction] =
    useActionBlocker(handleClearState);

  const fieldArrayRender = useCallback(
    (fieldArray: Readonly<FieldArrayRenderProps>) => {
      const handleCreateSubmit = handleCreateSubmitFactory({
        createInForm: (
          value: Readonly<ResolvableServiceUsageForm_ResolvableServiceUsage>,
        ) => {
          fieldArray.push({
            ...value,
            index: fields.serviceUsages.value?.length ?? 0,
          });
        },
      });

      const handleFlushAllSubmit = handleFlushAllSubmitFactory({
        flushAllInForm: () =>
          void setFieldValue(fields.serviceUsages.name, null),
      });

      const handleFlushOneSubmit = handleFlushOneSubmitFactory({
        flushOne: (index: number) => {
          const existing = fields.serviceUsages.value?.[index];

          invariant(
            !!existing &&
              existing.contentOriginId === contentOriginsCodeMap.Modified._id &&
              !!existing.serviceUsageOrigin,
            'Invalid state: only customized usages can be flushed',
          );

          const newValue = {
            _id: existing._id,
            contentOriginId: contentOriginsCodeMap.Catalog._id,
            serviceUsageOrigin: existing.serviceUsageOrigin,
            index,
          };

          return newValue as ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage>;
        },
        updateInForm: (
          index: number,
          newValue: ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage>,
        ) => {
          fieldArray.replace(index, newValue);
        },
      });

      const handleCustomizeAllSubmit = handleCustomizeAllSubmitFactory({
        customizeAllInForm: () =>
          void setFieldValue(
            fields.serviceUsages.name,
            fallbackResolvableServiceUsages?.map((item, i) => ({
              ...item,
              index: i,
            })),
          ),
      });

      const handleCustomizeOneSubmit = handleCustomizeOneSubmitFactory({
        customizeOne: (
          index: number,
        ): ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage> => {
          const existing = fields.serviceUsages.value?.[index];

          invariant(
            !!existing &&
              existing.contentOriginId === contentOriginsCodeMap.Catalog._id &&
              !!existing.serviceUsageOrigin,
            'Invalid state: only catalog usages can be customized',
          );

          const origin = fallbackResolvableServiceUsages?.find(
            (fallbackResolvableServiceUsage) =>
              fallbackResolvableServiceUsage._id ===
              existing.serviceUsageOrigin?.serviceUsageId,
          );

          invariant(!!origin, 'Invalid state: usage origin not found');

          const newValue = {
            ...origin,
            _id: existing._id,
            contentOriginId: contentOriginsCodeMap.Modified._id,
            index,
            serviceUsageOrigin: existing.serviceUsageOrigin,
          };

          return newValue as ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage>;
        },
        updateInForm: (
          index: number,
          newValue: ReadonlyDeep<ResolvableServiceUsageForm_ResolvableServiceUsage>,
        ) => {
          fieldArray.replace(index, newValue);
        },
      });

      const handleRemoveSubmit = handleRemoveSubmitFactory({
        removeInForm: (removedIndex: number) => {
          const reindexed = (fields.serviceUsages.value ?? [])
            .filter((_, i) => i !== removedIndex)
            .map((item, i) => ({ ...item, index: i }));

          void setFieldValue(fields.serviceUsages.name, reindexed);
        },
      });

      const handleUpdateSubmit = handleUpdateSubmitFactory({
        updateInForm: (
          index: number,
          value: Readonly<ResolvableServiceUsageForm_ResolvableServiceUsage>,
        ) => {
          fieldArray.replace(index, value);
        },
      });

      return (
        <>
          <ServiceUsages
            consultResolvableServiceInNewTab
            editionMode
            extraCards={extraCards}
            fallbackServiceUsages={pFallbackResolvableServiceUsages}
            globalTotal={globalTotal}
            globalTotalErrorMessages={globalTotalErrorMessages}
            groupBanner={
              <ServiceUsagesGroupBanner
                editionMode={isInGroup ? !mustInherit : false}
                groupingMode={groupingMode}
                isOptional={groupIsOptional}
                label={groupLabel}
                onStartCreateGroup={onStartCreateGroup}
                onStartRemoveGroup={isInGroup ? onStartRemoveGroup : undefined}
                onStartRenameGroup={isInGroup ? onStartRenameGroup : undefined}
                onStartReorder={onStartReorder}
                onStartSwitchGroupingMode={onStartSwitchGroupingMode}
                sectionLabel={groupSectionLabel}
              />
            }
            isInGroup={isInGroup}
            isInResolvableMode
            manualTotal={manualTotal}
            maskAmounts={maskAmounts}
            onStartCreate={handleStartCreate}
            onStartCustomizeAll={handleStartCustomizeAll}
            onStartCustomizeOne={handleStartCustomizeOne}
            onStartFlushAll={handleStartFlushAll}
            onStartFlushOne={handleStartFlushOne}
            onStartMove={onStartMove}
            onStartRemove={handleStartRemove}
            onStartUpdate={handleStartUpdate}
            queryKeysToInvalidate={queryKeysToInvalidate}
            resolvableFieldSettings={resolvableFieldSettings}
            serviceUsages={fields.serviceUsages.value}
            serviceUsagesGroupId={serviceUsagesGroupId}
            serviceUserId={useDirectMutation ? serviceUserId : undefined}
            showInheritanceAlert={showInheritanceAlert}
            showTotalCalculation={showTotalCalculation}
            switchTotalModeMutation={
              useDirectMutation ? mutations.switchTotalMode : undefined
            }
            totalCalculation={totalCalculation}
            totalModeResolvableFieldSettings={resolvableFieldSettings}
            updateManualTotalMutation={
              useDirectMutation ? mutations.updateManualTotal : undefined
            }
          />
          {/* NB : We don't want to nest forms in HTML, so we extract these in a portal */}
          {createPortal(
            <>
              <DeleteResolvableServiceUsageForm
                askMemoBeforeSubmit={useDirectMutation}
                isDialogOpen={state.isRemoveDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleRemoveSubmit}
                useDirectMutation={useDirectMutation}
              />
              <FlushResolvableServiceUsagesForm
                askMemoBeforeSubmit={useDirectMutation}
                cardinality="all"
                isDialogOpen={state.isFlushAllDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleFlushAllSubmit}
                useDirectMutation={useDirectMutation}
              />
              <FlushResolvableServiceUsagesForm
                askMemoBeforeSubmit={useDirectMutation}
                cardinality="one"
                isDialogOpen={state.isFlushOneDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleFlushOneSubmit}
                useDirectMutation={useDirectMutation}
              />
              <CustomizeResolvableServiceUsagesForm
                askMemoBeforeSubmit={useDirectMutation}
                cardinality="all"
                isDialogOpen={state.isCustomizeAllDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleCustomizeAllSubmit}
                useDirectMutation={useDirectMutation}
              />
              <CustomizeResolvableServiceUsagesForm
                askMemoBeforeSubmit={useDirectMutation}
                cardinality="one"
                isDialogOpen={state.isCustomizeOneDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleCustomizeOneSubmit}
                useDirectMutation={useDirectMutation}
              />
            </>,
            document.body,
          )}
          <RightDrawer
            isOpen={state.isCreateDrawerOpen || state.isUpdateDrawerOpen}
            onDrawerClose={blockableClearStateHandler}
          >
            <ResolvableServiceUsageForm
              askMemoBeforeSubmit={useDirectMutation}
              documentResolversSettings={documentResolversSettings}
              formVariant={
                state.isUpdateDrawerOpen
                  ? FormVariant.Modification
                  : FormVariant.Creation
              }
              index={
                (state.isUpdateDrawerOpen
                  ? serviceUsageInitialValue?.index
                  : fields.serviceUsages.value?.length) ?? 0
              }
              initialValues={serviceUsageInitialValue}
              onCancel={blockableClearStateHandler}
              onSubmit={
                state.isUpdateDrawerOpen
                  ? handleUpdateSubmit
                  : handleCreateSubmit
              }
              resolvableFieldSettings={resolvableFieldSettings}
              setMustBlockAction={!paths ? setMustBlockAction : undefined}
              showCancelButton
              storage={storage}
              title={
                state.isUpdateDrawerOpen
                  ? "Formulaire de modification d'une Prestation"
                  : "Formulaire de création d'une Prestation"
              }
            />
          </RightDrawer>
        </>
      );
    },
    [
      blockableClearStateHandler,
      contentOriginsCodeMap.Catalog._id,
      contentOriginsCodeMap.Modified._id,
      documentResolversSettings,
      extraCards,
      fallbackResolvableServiceUsages,
      fields,
      groupingMode,
      globalTotal,
      globalTotalErrorMessages,
      groupIsOptional,
      groupLabel,
      groupSectionLabel,
      isInGroup,
      manualTotal,
      maskAmounts,
      mustInherit,
      mutations.switchTotalMode,
      mutations.updateManualTotal,
      onStartMove,
      onStartRemoveGroup,
      onStartCreateGroup,
      onStartRenameGroup,
      onStartReorder,
      onStartSwitchGroupingMode,
      queryKeysToInvalidate,
      showInheritanceAlert,
      showTotalCalculation,
      handleClearState,
      handleCreateSubmitFactory,
      handleFlushAllSubmitFactory,
      handleFlushOneSubmitFactory,
      handleCustomizeAllSubmitFactory,
      handleCustomizeOneSubmitFactory,
      handleRemoveSubmitFactory,
      handleStartCreate,
      handleStartCustomizeAll,
      handleStartCustomizeOne,
      handleStartFlushAll,
      handleStartFlushOne,
      handleStartRemove,
      handleStartUpdate,
      handleUpdateSubmitFactory,
      serviceUsageInitialValue,
      paths,
      pFallbackResolvableServiceUsages,
      resolvableFieldSettings,
      serviceUsagesGroupId,
      serviceUserId,
      setFieldValue,
      setMustBlockAction,
      state,
      storage,
      totalCalculation,
      useDirectMutation,
    ],
  );

  return { fieldArrayRender };
}
